/**
 * Unified unit conversion.
 * Routes a value + source unit to the correct converter based on unit type.
 * Used for weather legends and other contexts where values arrive with arbitrary units.
 */

import { Units } from '../units';
import { convertSpeed } from './speed/convert';
import { convertTemperature } from './temperature/convert';
import { convertPrecipitation } from './precipitation/convert';
import { convertDistance, convertHeight } from './length/convert';

export interface UnitConversionResult {
  value: number;
  unit: string;
}

type UnitCategory =
  | 'speed'
  | 'height'
  | 'distance'
  | 'precipitation'
  | 'temperature'
  | 'none';

/**
 * Categorize a unit string to determine which converter to use.
 */
function categorizeUnit(unit: string): UnitCategory {
  const normalized = unit.toLowerCase().trim();

  // Speed units
  if (
    [
      'mph',
      'm/s',
      'ms',
      'kn',
      'kt',
      'kts',
      'knots',
      'km/h',
      'kmh',
      'kph',
      'miles per hour',
    ].includes(normalized)
  ) {
    return 'speed';
  }

  // Height units (wave height, altitude — short distances)
  if (['ft', 'feet', 'foot', 'm', 'meters', 'metres'].includes(normalized)) {
    return 'height';
  }

  // Distance/visibility units (longer distances)
  if (
    [
      'mi',
      'miles',
      'km',
      'kilometers',
      'kilometres',
      'nm',
      'nmi',
      'nautical miles',
    ].includes(normalized)
  ) {
    return 'distance';
  }

  // Precipitation (depth or rate)
  if (
    ['in', 'inches', 'mm', 'in/hr', 'in/h', 'mm/hr', 'mm/h'].includes(
      normalized,
    )
  ) {
    return 'precipitation';
  }

  // Temperature
  if (
    [
      'c',
      '°c',
      'celsius',
      'f',
      '°f',
      'fahrenheit',
      'fahrentheit',
      'k',
      'kelvin',
    ].includes(normalized)
  ) {
    return 'temperature';
  }

  return 'none';
}

/**
 * Convert any value with a known unit to the target unit system.
 * Returns null if no conversion applies (unit unchanged).
 */
export function convertUnit(
  value: number,
  sourceUnit: string,
  targetSystem: Units,
): UnitConversionResult | null {
  const category = categorizeUnit(sourceUnit);

  if (category === 'none') return null;

  let result: { value: number; unit: string };

  switch (category) {
    case 'speed':
      result = convertSpeed(value, sourceUnit, targetSystem);
      break;
    case 'height':
      result = convertHeight(value, sourceUnit, targetSystem);
      break;
    case 'distance':
      result = convertDistance(value, sourceUnit, targetSystem);
      break;
    case 'precipitation':
      result = convertPrecipitation(value, sourceUnit, targetSystem);
      break;
    case 'temperature':
      result = convertTemperature(value, sourceUnit, targetSystem);
      break;
  }

  // If the unit didn't change, no conversion was needed
  if (result.unit === sourceUnit) return null;

  return {
    value: Math.round(result.value * 100) / 100,
    unit: result.unit,
  };
}

/**
 * Get the display unit for a source unit in the target system.
 */
export function getDisplayUnit(
  sourceUnit: string,
  targetSystem: Units,
): string {
  // Use a dummy value to determine target unit
  const result = convertUnit(1, sourceUnit, targetSystem);
  return result ? result.unit : sourceUnit;
}
